import { 
    USER_LOGOUT, 
    USER_REGISTER_FAIL, 
    USER_REGISTER_REQUEST, 
    USER_REGISTER_SUCCESS, 
    USER_SIGNIN_FAIL, 
    USER_SIGNIN_REQUEST, 
    USER_SIGNIN_SUCCESS,
    OTP_VERIFICATION_REQUEST,
    OTP_VERIFICATION_SUCCESS,
    OTP_VERIFICATION_FAIL } from "../Constants/userConstants"
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, RecaptchaVerifier, signInWithPhoneNumber, linkWithCredential, EmailAuthProvider, sendEmailVerification } from 'firebase/auth';
import { collection, doc, setDoc, getDoc, Timestamp, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firebaseAuth, firestore, storage } from "../firebase";
import { loadCartFromFirestore } from './cartActions';

// Step 1: Validate registration data (like Android SignUpFragment)
export const validateRegistrationData = (name, mobile, email, password, role = 'user', city = '', state = '', retailerData = null) => async(dispatch) => {
    dispatch({type: USER_REGISTER_REQUEST, payload: {email, password} });

    try {
        // Step 1: Check if email already exists (like Android fetchSignInMethodsForEmail)
        const emailQuery = query(collection(firestore, "USERS"), where('Email', "==", email));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
            dispatch({
                type: USER_REGISTER_FAIL,
                payload: "Email already exists!"
            });
            return null;
        }

        // Step 2: Check if mobile number already exists (like Android mobile check)
        const mobileQuery = query(collection(firestore, "USERS"), where('Mobile_No', "==", mobile));
        const mobileSnapshot = await getDocs(mobileQuery);
        
        if (!mobileSnapshot.empty) {
            dispatch({
                type: USER_REGISTER_FAIL,
                payload: "Phone Number already exists!"
            });
            return null;
        }

        // Step 3: For retailers, check if document number already exists
        if (role === 'retailer' && retailerData) {
            const docQuery = query(collection(firestore, "USERS"), where('Business_Proof_No', "==", retailerData.documentNumber));
            const docSnapshot = await getDocs(docQuery);
            
            if (!docSnapshot.empty) {
                dispatch({
                    type: USER_REGISTER_FAIL,
                    payload: "A seller with this document number already exists!"
                });
                return null;
            }
        }

        // Step 4: If validation passes, return registration data for OTP verification
        const registrationData = {
            name, mobile, email, password, role, city, state,
            ...(retailerData && { retailerData })
        };

        dispatch({type: USER_REGISTER_SUCCESS, payload: { validationPassed: true }});
        return registrationData;

    } catch(error) {
        console.error('Validation error:', error);
        dispatch({
            type: USER_REGISTER_FAIL,
            payload: error.message
        });
        return null;
    }
}

// Step 1.5: Send OTP to mobile number (Production Ready)
export const sendOTP = (mobile) => async(dispatch) => {
    try {
        // Normalize and format phone number to E.164 (+91XXXXXXXXXX)
        const toE164India = (raw) => {
            if (!raw) return null;
            // Trim and remove all non-digits
            let digits = String(raw).trim().replace(/[^0-9+]/g, '');
            // If it already starts with +, assume user provided E.164
            if (digits.startsWith('+')) return digits;
            // Remove any leading zeros
            digits = digits.replace(/^0+/, '');
            // Expect 10 digits for India local numbers
            if (digits.length !== 10) return null;
            return `+91${digits}`;
        };

        const phoneNumber = toE164India(mobile);
        if (!phoneNumber) {
            throw { code: 'auth/invalid-phone-number', message: 'Invalid mobile number. Enter 10 digits (e.g., 9876566730).' };
        }
        
        // Ensure the container exists and is visible
        let recaptchaContainer = document.getElementById('recaptcha-container');
        if (!recaptchaContainer) {
            throw new Error('reCAPTCHA container not found. Please ensure you are on the registration page.');
        }

        // Clear any existing verifier to start fresh
        if (window._sbRecaptchaVerifier) {
            try {
                window._sbRecaptchaVerifier.clear();
            } catch (e) {
                console.log('Error clearing existing verifier:', e);
            }
            window._sbRecaptchaVerifier = null;
            window._sbRecaptchaRendered = false;
        }

        // Create a new RecaptchaVerifier
        window._sbRecaptchaVerifier = new RecaptchaVerifier(
            firebaseAuth,
            'recaptcha-container',
            {
                size: 'normal', // visible challenge for reliability
                callback: (response) => {
                    console.log('reCAPTCHA solved:', response);
                },
                'expired-callback': () => {
                    console.log('reCAPTCHA expired');
                    try { window._sbRecaptchaVerifier?.reset?.(); } catch (_) {}
                }
            }
        );

        // Render the reCAPTCHA widget
        console.log('Rendering reCAPTCHA...');
        await window._sbRecaptchaVerifier.render();
        window._sbRecaptchaRendered = true;
        console.log('reCAPTCHA rendered successfully');

        // Try once, then on Timeout/element-removed retry by resetting and re-rendering
        const attemptSend = async () => {
            return await signInWithPhoneNumber(
                firebaseAuth,
                phoneNumber,
                window._sbRecaptchaVerifier
            );
        };

        let confirmationResult;
        try {
            console.log('Attempting to send OTP to:', phoneNumber);
            confirmationResult = await attemptSend();
            console.log('OTP sent successfully');
        } catch (err) {
            console.error('=== FULL ERROR OBJECT ===');
            console.error(err);
            console.error('=== ERROR DETAILS ===');
            console.error('Error code:', err.code);
            console.error('Error message:', err.message);
            console.error('Error name:', err.name);
            if (err.customData) {
                console.error('Custom data:', err.customData);
            }
            const msg = String(err?.message || '').toLowerCase();
            if (msg.includes('timeout') || msg.includes('element') || msg.includes('removed')) {
                try { window._sbRecaptchaVerifier?.reset?.(); } catch (_) {}
                // Re-render to ensure widget is active
                await window._sbRecaptchaVerifier.render();
                confirmationResult = await attemptSend();
            } else {
                throw err;
            }
        }

        // Store confirmation result globally for verification step
        window.confirmationResult = confirmationResult;
        return { success: true, message: 'OTP sent to your mobile number' };
        
    } catch (error) {
        // Reset reCAPTCHA on error
        if (window._sbRecaptchaVerifier) {
            try {
                window._sbRecaptchaVerifier.clear();
            } catch (e) {
                console.log('Error clearing reCAPTCHA:', e);
            }
            window._sbRecaptchaVerifier = null;
            window._sbRecaptchaRendered = false;
        }
        
        // Log full error for debugging
        console.error('=== CAUGHT ERROR IN CATCH BLOCK ===');
        console.error('Full error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        // Provide specific error messages for common issues
        let errorMessage = error.message;
        
        if (error.code === 'auth/argument-error') {
            errorMessage = 'Domain not authorized for phone authentication. Please configure Firebase Console to add your domain to authorized domains.';
        } else if (error.code === 'auth/web-storage-unsupported') {
            errorMessage = 'Web storage is not supported or disabled. Please enable cookies and local storage.';
        } else if (error.code === 'auth/operation-not-allowed') {
            errorMessage = 'Phone authentication is not enabled. Please enable it in Firebase Console.';
        } else if (error.code === 'auth/captcha-check-failed') {
            errorMessage = 'reCAPTCHA verification failed. Please try again.';
        } else if (error.code === 'auth/invalid-phone-number') {
            errorMessage = 'Invalid phone number format. Please enter a valid mobile number.';
        } else if (error.code === 'auth/missing-phone-number') {
            errorMessage = 'Phone number is required.';
        } else if (error.code === 'auth/quota-exceeded') {
            errorMessage = 'SMS quota exceeded. Please try again later.';
        } else if (error.code === 'auth/too-many-requests') {
            errorMessage = 'Too many registration attempts. Please wait 1-2 hours or use a different phone number. Alternatively, add this number as a test phone number in Firebase Console for instant verification.';
        } else if (error.code === 'auth/invalid-phone-number') {
            errorMessage = 'Invalid phone number. Please enter 10 digits (e.g., 9835667305).';
        }
        
        throw new Error(`Failed to send OTP: ${errorMessage}`);
    }
};

// Step 2: Complete registration after OTP verification (like Android OTPFragment)
export const verifyOTPAndCompleteRegistration = (otp, registrationData) => async(dispatch) => {
    dispatch({type: OTP_VERIFICATION_REQUEST});

    try {
        const { name, mobile, email, password, role, city, state, retailerData } = registrationData;
        const coords = registrationData?.coords || null;

        // Step 1: Verify OTP (exactly like Android OTPFragment)
        
        if (!window.confirmationResult) {
            dispatch({
                type: OTP_VERIFICATION_FAIL,
                payload: "OTP session expired. Please request a new OTP."
            });
            return;
        }

        // Step 2: Verify the OTP and get phone credential (like Android PhoneAuthProvider)
        const phoneCredential = await window.confirmationResult.confirm(otp);
        
        // Get the phone auth user (this is the primary user)
        const phoneUser = phoneCredential.user;

        // Step 3: Create email credential (like Android EmailAuthProvider)
        const emailCredential = EmailAuthProvider.credential(email, password);

        // Step 4: Link phone credential with email credential (like Android linkWithCredential)
        const linkedUserCredential = await linkWithCredential(phoneUser, emailCredential);
        
        const uid = linkedUserCredential.user.uid;

        let documentUrl = "";
        
        // Step 2: For retailers, upload document to Firebase Storage
        if (role === 'retailer' && retailerData && retailerData.documentFile) {
            const file = retailerData.documentFile;
            const filePath = `seller_docs/${uid}/${Date.now()}_${file.name}`;
            const fileRef = storageRef(storage, filePath);
            
            await uploadBytes(fileRef, file);
            documentUrl = await getDownloadURL(fileRef);
        }

        // Step 5: Create comprehensive user document (exactly like Android OTPFragment)
        const userData = {
            // Core identity
            Mobile_No: mobile,
            Full_Name: role === 'retailer' && retailerData ? retailerData.ownerName : name,
            Email: email,
            Profile: "",
            id: uid,

            // Account meta
            Account_Created_On: serverTimestamp(),
            'Last seen': Timestamp.fromDate(new Date()),

            // Business/profile
            Shop_Name: role === 'retailer' && retailerData ? retailerData.ownerName : name,
            Business_Proof_No: role === 'retailer' && retailerData ? retailerData.documentNumber : "",
            Business_Proof_Image: documentUrl || "",
            Business_Proof_Type: role === 'retailer' && retailerData ? retailerData.documentType : "",
            Role: role || 'user',
            
            // Retailer categories (matching Android field name)
            Retailer_Categories: role === 'retailer' && retailerData && retailerData.selectedCategories 
                ? retailerData.selectedCategories 
                : [],

            // Bank placeholders
            Account_Holder_Name: role === 'retailer' && retailerData ? retailerData.ownerName : name,
            Account_No: "",
            IFSC: "",
            Bank_Name: role === 'retailer' ? "" : "user",
            Branch_Name: role === 'retailer' ? "" : "user",
            isBankDetailAvailable: false,

            // Location (store both cases for web/Android compatibility)
            city: city || "",
            state: state || "",
            country: "india",
            latitude: coords && typeof coords.lat === 'number' ? coords.lat : null,
            longitude: coords && typeof coords.lng === 'number' ? coords.lng : null,

            // Address placeholders
            Pincode: 0,
            FullAddress: "",
            Landmark: "",
            Address: `${city || ''}${city && state ? ', ' : ''}${state || ''}`,

            // Flags
            isVerifiedBusiness: false,
            IsAdmin: false,
            Email_Verified: false, // Will be verified via email link
            Mobile_Verified: true, // OTP verified
            Phone_Number: `+91${mobile}`,
            Account_Status: role === 'user' ? true : false // Users active, retailers need approval
        };

        // Step 3: Save user data to Firestore
        await setDoc(doc(firestore, "USERS", uid), userData);

        // Step 6: Create USER_DATA subcollection docs (Android structure)
        // USERS/{uid}/USER_DATA/{MY_*} => { list_size: 0 }
        const userDataDocs = [
            "MY_WISHLIST",
            "MY_RATINGS",
            "MY_CART",
            "MY_ADDRESSES",
            "MY_NOTIFICATIONS",
            "MY_BANK",
        ];
        for (const name of userDataDocs) {
            await setDoc(doc(firestore, "USERS", uid, "USER_DATA", name), { list_size: 0 });
        }

        // Step 5: Send email verification (like Android)
        try {
            const actionCodeSettings = {
                url: `${window.location.origin}/verify-email?uid=${encodeURIComponent(uid)}`,
                handleCodeInApp: true
            };
            await sendEmailVerification(linkedUserCredential.user, actionCodeSettings);
        } catch (emailError) {
            // Don't fail the registration if email verification fails
        }

        // Step 7: Refetch user doc to get resolved serverTimestamp for Account_Created_On
        let userInfoDoc = userData;
        try {
            const freshSnap = await getDoc(doc(firestore, "USERS", uid));
            if (freshSnap.exists()) {
                userInfoDoc = freshSnap.data();
                // Ensure both id and uid are present for compatibility
                userInfoDoc.id = uid;
                userInfoDoc.uid = uid;
            }
        } catch(_) {}

        // Store in localStorage and Redux
        localStorage.setItem('userInfo', JSON.stringify(userInfoDoc));
        
        dispatch({type: OTP_VERIFICATION_SUCCESS, payload: userInfoDoc});
        dispatch({type: USER_SIGNIN_SUCCESS, payload: userInfoDoc});

    } catch(error) {
        console.error('OTP verification error:', error);
        let errorMessage = error.message;
        
        // Handle specific Firebase Auth errors
        if (error.code === 'auth/email-already-in-use') {
            errorMessage = "Email already exists!";
        } else if (error.code === 'auth/weak-password') {
            errorMessage = "Password should be at least 6 characters";
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = "Invalid email address";
        }

        dispatch({
            type: OTP_VERIFICATION_FAIL,
            payload: errorMessage
        });
    }
}


// Enhanced signin supporting both email and mobile (like Android app)
export const signin = (emailOrMobile, password) => async(dispatch) => {
    dispatch({type: USER_SIGNIN_REQUEST, payload: {emailOrMobile, password} });

    try {
        let userEmail = emailOrMobile;
        let userData = null;

        // Check if input is mobile number (10 digits) or email
        const isMobileNumber = /^\d{10}$/.test(emailOrMobile);
        
        if (isMobileNumber) {
            // If mobile number, find the associated email
            const mobileQuery = query(collection(firestore, "USERS"), where('Mobile_No', "==", emailOrMobile));
            const mobileSnapshot = await getDocs(mobileQuery);
            
            if (mobileSnapshot.empty) {
                dispatch({
                    type: USER_SIGNIN_FAIL,
                    payload: "Mobile number not registered!"
                });
                return;
            }
            
            // Get email from user document
            mobileSnapshot.forEach((doc) => {
                userData = doc.data();
                userEmail = userData.Email;
            });
        } else {
            // If email, verify it exists in our database
            const emailQuery = query(collection(firestore, "USERS"), where('Email', "==", emailOrMobile));
            const emailSnapshot = await getDocs(emailQuery);
            
            if (emailSnapshot.empty) {
                dispatch({
                    type: USER_SIGNIN_FAIL,
                    payload: "Email not registered!"
                });
                return;
            }
            
            emailSnapshot.forEach((doc) => {
                userData = doc.data();
            });
        }

        // Check if account is active
        if (!userData.Account_Status) {
            // Store user data for pending approval page
            localStorage.setItem('pendingUserInfo', JSON.stringify(userData));
            dispatch({
                type: USER_SIGNIN_FAIL,
                payload: "ACCOUNT_PENDING" // Special flag for pending approval
            });
            return;
        }

        // Authenticate with Firebase Auth using email
        const userCredential = await signInWithEmailAndPassword(firebaseAuth, userEmail, password);
        
        // Add Firebase Auth UID to userData
        const userInfoWithUid = {
            ...userData,
            uid: userCredential.user.uid
        };
        
        // Success - dispatch actions immediately
        dispatch({type: USER_SIGNIN_SUCCESS, payload: userInfoWithUid});
        localStorage.setItem('userInfo', JSON.stringify(userInfoWithUid));
        
        // Load user's cart from Firestore after a short delay
        setTimeout(() => {
            dispatch(loadCartFromFirestore());
        }, 1000);

    } catch(error) {
        console.error('Signin error:', error);
        let errorMessage = error.message;
        
        // Handle specific Firebase Auth errors
        if (error.code === 'auth/wrong-password') {
            errorMessage = "Wrong Password";
        } else if (error.code === 'auth/user-not-found') {
            errorMessage = "Account not found!";
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = "Invalid email format";
        } else if (error.code === 'auth/user-disabled') {
            errorMessage = "Account has been disabled";
        } else if (error.code === 'auth/too-many-requests') {
            errorMessage = "Too many failed attempts. Please try again later";
        }

        dispatch({
            type: USER_SIGNIN_FAIL,
            payload: errorMessage
        });
    }
}


export const signout = () => (dispatch) =>{
    try{
        localStorage.removeItem('userInfo');
        localStorage.removeItem('cartItems');
        localStorage.removeItem('shippingAddress');
    } catch (_) {}

    // Perform Firebase sign out; regardless of outcome, proceed to clear redux and navigate
    Promise.resolve(signOut(firebaseAuth))
        .catch(() => {})
        .finally(() => {
            dispatch({type: USER_LOGOUT});
            // Redirect to homepage and refresh
            try {
                if (typeof window !== 'undefined' && window.location) {
                    if (window.location.pathname !== '/') {
                        window.location.replace('/');
                    } else {
                        window.location.reload();
                    }
                }
            } catch (_) {}
        });
}